import React from 'react';
import {
  Steps,
  Divider,
  Form,
  Alert,
  Button,
  ButtonType,
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Typography, Input } from 'antd';
import { capitalize } from 'lodash';
import { WizardStepSubTitle } from 'views/Integrations/subviews/WizardStepSubTitle';

const { Title } = Typography;
const { Step } = Steps;
const { Password } = Input;

interface BasicAuthWizardArticleProps {
  t: any;
  steps: any;
  partner_id: string;
  translatedLogin: string;
  translatedCreds: string;
  userInputValue: string;
  setUserInputValue: (val: string) => void;
  passwordInputValue: string;
  setPasswordInputValue: (val: string) => void;
  isEmptyOrSpaces: (val: string) => boolean;
  handleSubmit: () => void;
  history: any;
  RoutePaths: any;
  PartnerModule: any;
}

export const BasicAuthWizardArticle: React.FC<BasicAuthWizardArticleProps> = ({
  t,
  steps,
  partner_id,
  translatedLogin,
  translatedCreds,
  userInputValue,
  setUserInputValue,
  passwordInputValue,
  setPasswordInputValue,
  isEmptyOrSpaces,
  handleSubmit,
  history,
  RoutePaths,
  PartnerModule,
}) => (
  <article
    data-cy="integration-wizard"
    className="wizard flex flex-col items-stretch relative h-100% min-h-525px min-w-[320px] md:w-65% md:m-auto p-24 pb-0 bg-white rounded-10"
  >
    <header className="wizard__header text-left">
      <Title level={4}> {t('Integration.configure')}</Title>
    </header>
    <main className="wizard__main flex flex-col items-stretch flex-1 min-h-0 pb-0 mb-60">
      <Steps data-cy="wizard-steps" className="wizard__steps py-15" current={0} size="small">
        {steps?.map((step: any, index: number) => (
          <Step
            key={`wizard-pane-${step.name}`}
            title={t(step.name)}
            className="text-ellipsis overflow-hidden"
          />
        ))}
      </Steps>
      <Divider className="m-0" />
      <main className="wizard__body flex-1 min-h-0 overflow-auto">
        <div data-cy="wizard-items-comp" className="h-100% flex flex-col items-stretch">
          <WizardStepSubTitle
            subTitle={`${translatedLogin} ${capitalize(partner_id)} ${translatedCreds}`}
          />
          <div className="w-55% min-w-250 min-h-0 mx-auto">
            <Form layout="vertical" className="my-10">
              <Form.Item
                label={PartnerModule.get_username_label(partner_id)}
                required
                className="flex justify-start mb-15"
                htmlFor="username"
              >
                <Input
                  id="username"
                  type="text"
                  data-cy="integration_username_input"
                  data-testid="integration_username_input"
                  placeholder={PartnerModule.get_username_label(partner_id)}
                  value={userInputValue}
                  autoComplete="false"
                  allowClear={true}
                  onChange={(e) => setUserInputValue(e.target.value)}
                />
              </Form.Item>
              <Form.Item
                label={PartnerModule.get_password_label(partner_id)}
                required
                className="mb-15"
                htmlFor="user_password"
              >
                <Password
                  data-cy="integration_password_input"
                  data-testid="integration_password_input"
                  id="user_password"
                  placeholder={PartnerModule.get_password_label(partner_id)}
                  value={passwordInputValue}
                  autoComplete="false"
                  allowClear={true}
                  onChange={(e) => setPasswordInputValue(e.target.value)}
                />
              </Form.Item>
              {PartnerModule.get_auth_hint(partner_id) && (
                <Alert
                  type="info"
                  showIcon={true}
                  style={{ border: 'none' }}
                  className="text-left flex flex-row items-center w-100% my-28 rounded-10"
                  message={PartnerModule.get_auth_hint(partner_id)}
                />
              )}
            </Form>
          </div>
        </div>
      </main>
    </main>
    <footer
      data-cy="wizard-footer"
      className="wizard__footer absolute left-0 bottom-0 w-100% text-left"
    >
      <Divider className="m-0" />
      <div className="flex justify-between flex-row-reverse flex-wrap align-center px-24 py-12">
        <div className="flex flex-end flex-wrap align-center">
          <Button
            size="middle"
            type={ButtonType.primary}
            data-cy="configure_button"
            data-testid="configure_button"
            disabled={isEmptyOrSpaces(userInputValue) || isEmptyOrSpaces(passwordInputValue)}
            onClick={() => handleSubmit()}
          >
            {t('Integration.submit')}
          </Button>
        </div>
        <Button
          data-cy="wizard-action-cancel"
          size="middle"
          type={ButtonType.outline}
          style={{ margin: '0 8px' }}
          onClick={() => history.push(RoutePaths.Integrations().toString())}
        >
          {t('Integration.cancel')}
        </Button>
      </div>
    </footer>
  </article>
);

export default BasicAuthWizardArticle;
